// Story types and interfaces

export type StoryType =
  | 'first_day_school'
  | 'loss_parent'
  | 'loss_pet'
  | 'illness_treatment'
  | 'bullying'
  | 'moving_house'
  | 'new_sibling'
  | 'divorce'
  | 'anxiety'
  | 'other';

export interface StoryTypeOption {
  value: StoryType;
  label: string;
  emoji: string;
  description: string;
  prompts: string[];
  color: string;
}

export const STORY_TYPES: StoryTypeOption[] = [
  {
    value: 'first_day_school',
    label: 'First Day at School',
    emoji: '🏫',
    description: 'Help your child prepare for their exciting first day',
    color: 'from-blue-400 to-blue-600',
    prompts: [
      "What is your child's name?",
      "What school will they be attending?",
      "What are they most excited or nervous about?",
      "Any special friends or teachers they should know about?",
    ],
  },
  {
    value: 'loss_parent',
    label: 'Loss of a Parent',
    emoji: '💔',
    description: 'A gentle story to help process grief and memories',
    color: 'from-purple-400 to-purple-600',
    prompts: [
      "What is your child's name?",
      "Share a special memory about the parent",
      "What qualities did the parent have?",
      "How can we honor their memory?",
    ],
  },
  {
    value: 'loss_pet',
    label: 'Loss of a Pet',
    emoji: '🐾',
    description: 'Helping children understand and cope with losing a beloved pet',
    color: 'from-amber-400 to-amber-600',
    prompts: [
      "What is your child's name?",
      "What was the pet's name and type?",
      "What made this pet special?",
      "Share a favorite memory together",
    ],
  },
  {
    value: 'illness_treatment',
    label: 'Medical Treatment',
    emoji: '🏥',
    description: 'Making medical procedures less scary and more understandable',
    color: 'from-green-400 to-green-600',
    prompts: [
      "What is your child's name?",
      "What treatment or procedure are they facing?",
      "What questions or worries do they have?",
      "What can help them feel brave?",
    ],
  },
  {
    value: 'bullying',
    label: 'Dealing with Bullying',
    emoji: '😢',
    description: 'Empowering children to handle bullying with confidence',
    color: 'from-red-400 to-red-600',
    prompts: [
      "What is your child's name?",
      "What kind of bullying are they experiencing?",
      "What are their strengths and talents?",
      "Who can they turn to for help?",
    ],
  },
  {
    value: 'moving_house',
    label: 'Moving to a New Home',
    emoji: '🏠',
    description: 'Making transitions easier and exciting',
    color: 'from-pink-400 to-pink-600',
    prompts: [
      "What is your child's name?",
      "Where are you moving to?",
      "What will they miss about the old home?",
      "What are they excited about in the new home?",
    ],
  },
  {
    value: 'new_sibling',
    label: 'Welcoming a New Sibling',
    emoji: '👶',
    description: 'Helping children adjust to a new brother or sister',
    color: 'from-yellow-400 to-yellow-600',
    prompts: [
      "What is your child's name?",
      "What is the new baby's name (or expected name)?",
      "How does your child feel about becoming a sibling?",
      "What special role can they have?",
    ],
  },
  {
    value: 'divorce',
    label: 'Parents Separating',
    emoji: '💑',
    description: 'Reassuring children during family changes',
    color: 'from-indigo-400 to-indigo-600',
    prompts: [
      "What is your child's name?",
      "What do they need to understand about the separation?",
      "How will both parents still be there for them?",
      "What won't change in their life?",
    ],
  },
  {
    value: 'anxiety',
    label: 'Managing Anxiety',
    emoji: '😰',
    description: 'Tools and techniques for coping with worries',
    color: 'from-teal-400 to-teal-600',
    prompts: [
      "What is your child's name?",
      "What situations make them anxious?",
      "What calming activities do they enjoy?",
      "What positive affirmations can help?",
    ],
  },
  {
    value: 'other',
    label: 'Other Situation',
    emoji: '✨',
    description: 'Create a custom story for any other life challenge',
    color: 'from-gray-400 to-gray-600',
    prompts: [
      "What is your child's name?",
      "What situation are they facing?",
      "What do you want them to learn or feel?",
      "Any specific details to include?",
    ],
  },
];

export interface StoryFormData {
  story_type: StoryType | null;
  title: string;
  story_input: string;
  word_count: 2500 | 5000;
  child_name?: string;
}

export const WORD_COUNT_OPTIONS = [
  {
    value: 2500,
    credits: 1,
    label: 'Short Story',
    duration: '~12 minutes',
    description: 'Perfect for bedtime or quick listening',
  },
  {
    value: 5000,
    credits: 2,
    label: 'Full Story',
    duration: '~25 minutes',
    description: 'More detailed and immersive experience',
  },
] as const;
